import { createStitches } from '@stitches/react';
import type * as Stitches from '@stitches/react';

const darkBaseRgb = '20, 18, 32';

const baseVariants = {
  dark: `rgb(${darkBaseRgb})`,
  darkAlpha100: `rgba(${darkBaseRgb}, 0.1)`,
  darkAlpha200: `rgba(${darkBaseRgb}, 0.2)`,
  darkAlpha300: `rgba(${darkBaseRgb}, 0.3)`,
  darkAlpha400: `rgba(${darkBaseRgb}, 0.4)`,
  darkAlpha500: `rgba(${darkBaseRgb}, 0.5)`,
  darkAlpha600: `rgba(${darkBaseRgb}, 0.6)`,
  darkAlpha700: `rgba(${darkBaseRgb}, 0.7)`,
  darkAlpha800: `rgba(${darkBaseRgb}, 0.8)`,
  darkAlpha900: `rgba(${darkBaseRgb}, 0.9)`,
};

const grayBase = '214, 10%';
const grays = {
  gray25: `hsl(${grayBase}, 99%)`,
  gray50: `hsl(${grayBase}, 98%)`,
  gray100: `hsl(${grayBase}, 95%)`,
  gray200: `hsl(${grayBase}, 90%)`,
  gray300: `hsl(${grayBase}, 85%)`,
  gray400: `hsl(${grayBase}, 80%)`,
  gray500: `hsl(${grayBase}, 75%)`,
  gray600: `hsl(${grayBase}, 70%)`,
  gray700: `hsl(${grayBase}, 65%)`,
  gray800: `hsl(${grayBase}, 60%)`,
  gray900: `hsl(${grayBase}, 55%)`,
};

export const colors = {
  ...grays,
  ldPurple: 'hsl(252, 78%, 60%)',
  dark: {
    ldPurple: 'hsl(252, 78%, 64%)',
    ...baseVariants,
    whiteAlpha50: 'rgb(29, 27, 41)', // Not actually an alpha color
    whiteAlpha100: 'rgb(38, 37, 49)', // Not actually an alpha color
    whiteAlpha200: 'hsl(245, 10%, 20%)',
    whiteAlpha300: 'hsl(245, 10%, 30%)',
    whiteAlpha400: 'hsl(245, 10%, 40%)',
  },
};

export const { styled, css, keyframes } = createStitches({
  theme: {
    colors: {
      gray500: 'hsl(206,10%,76%)',
      blue500: 'hsl(206,100%,50%)',
      purple500: 'hsl(252,78%,60%)',
      green500: 'hsl(148,60%,60%)',
      red500: 'hsl(352,100%,62%)',

      ldPurple: colors.ldPurple,
      ldPurpleDark: colors.dark.ldPurple,

      whiteAlpha50Dark: colors.dark.whiteAlpha50,
      whiteAlpha100Dark: colors.dark.whiteAlpha100,

      gray200: colors.gray200,
    },
    space: {
      1: '1px',
      2: '2px',
      3: '4px',
      4: '8px',
      5: '16px',
      6: '24px',
      7: '48px',
    },
    fontSizes: {
      0: '0.7em',
      1: '0.8em',
      2: '1em',
      3: '1.1em',
      4: '1.3em',
      5: '1.5em',
    },
    fonts: {
      mono: 'JetBrains Mono',
      serif: "Georgia, 'Times New Roman', Times, serif",
      heading: 'EB Garamond',
    },
    fontWeights: {},
    lineHeights: {},
    letterSpacings: {},
    sizes: {},
    borderWidths: {
      1: '1px',
      2: '2px',
      3: '4px',
    },
    borderStyles: {},
    radii: {
      1: '1px',
      2: '2px',
      3: '4px',
      4: '8px',
      5: '12px',
    },
    shadows: {},
    zIndices: {},
    transitions: {},
  },
  media: {
    sm: '(min-width: 30em)',
    md: '(min-width: 48em)',
    lg: '(min-width: 62em)',
    xl: '(min-width: 80em)',
    xxl: '(min-width: 96em)',
  },
  utils: {
    px: (value: Stitches.PropertyValue<'padding'>) => ({ paddingLeft: value, paddingRight: value }),
    py: (value: Stitches.PropertyValue<'padding'>) => ({ paddingTop: value, paddingBottom: value }),
    mx: (value: Stitches.PropertyValue<'margin'>) => ({ marginLeft: value, marginRight: value }),
    my: (value: Stitches.PropertyValue<'margin'>) => ({ marginTop: value, marginBottom: value }),
  },
});
